import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-loading-skeleton/dist/skeleton.css";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { IoCloseSharp } from "react-icons/io5";
import { BiSolidError } from "react-icons/bi";
import { RiDeleteBin6Fill } from "react-icons/ri";
import AdminPagination from "../../component/pagination/AdminPagination";
import SkeletonLoading from "../../component/SkeletonLoading";
import {
  useGetExternalChargesQuery,
  useDeleteExternalChargeMutation,
} from "../../store/api/externalChargeSlice";
import {
  capitalizeFirstLetter,
  formatDateToLocal,
} from "../../utils/dateAndTimeConversion";
import "../ManualPayment/ManualPaymentHistory.scss";

const toISODate = (d) => (d ? d.toISOString().split("T")[0] : undefined);

const initialPagination = { limit: 10, page: 1, total: "", totalPages: "" };

const statusClass = (status) => {
  if (!status) return "status unavailable";
  const s = status.toLowerCase();
  if (s === "completed") return "status bg-success text-white";
  if (s === "failed" || s === "cancelled") return "status bg-danger text-white";
  if (s === "pending") return "status unavailable";
  return "status unavailable";
};

const ReceivePaymentHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const [pagination, setPagination] = useState(initialPagination);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    from_date: null,
    to_date: null,
  });

  const [deleteExternalCharge, { isLoading: isDeleting }] =
    useDeleteExternalChargeMutation();

  const handleDelete = async () => {
    try {
      await deleteExternalCharge(deleteTargetId).unwrap();
      toast.success(t("charge_deleted"));
      setDeleteTargetId(null);
    } catch (err) {
      toast.error(err?.data?.message || t("failed_to_delete_charge"));
      setDeleteTargetId(null);
    }
  };

  const { data, isLoading, isFetching } = useGetExternalChargesQuery({
    page: pagination.page,
    limit: pagination.limit,
    status: filters.status || undefined,
    from_date: toISODate(filters.from_date),
    to_date: toISODate(filters.to_date),
  });

  const charges = data?.data?.charges || [];
  const pag = data?.data?.pagination;

  const handlePageChange = (page) =>
    setPagination((prev) => ({ ...prev, page }));
  const handleLimitChange = (limit) =>
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  const handleFilter = (key, val) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleReset = () => {
    setFilters({ status: "", from_date: null, to_date: null });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <>
      {/* Filters */}
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => handleFilter("status", e.target.value)}
            >
              <option value="">{t("allStatus")}</option>
              <option value="pending">{t("pending")}</option>
              <option value="completed">{t("completed")}</option>
              <option value="failed">{t("failed")}</option>
              <option value="cancelled">{t("cancelled")}</option>
            </select>
          </div>
          <div className="d-flex flex-grow-1">
            <DatePicker
              selected={filters.from_date}
              onChange={(d) => handleFilter("from_date", d)}
              selectsStart
              startDate={filters.from_date}
              endDate={filters.to_date}
              placeholder={`${t("from")} ${t("date")}`}
              dateFormat="dd/MM/yyyy"
              className="form-control mph-date-input"
              isClearable
            />
          </div>
          <div className="d-flex flex-grow-1">
            <DatePicker
              selected={filters.to_date}
              onChange={(d) => handleFilter("to_date", d)}
              selectsEnd
              startDate={filters.from_date}
              endDate={filters.to_date}
              minDate={filters.from_date}
              placeholder={`${t("to")} ${t("date")}`}
              dateFormat="dd/MM/yyyy"
              className="form-control mph-date-input"
              isClearable
            />
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button className="btn btn-primary addDriver" onClick={handleReset}>
              {t("btns.reset")}
            </button>
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th className="text-nowrap">{t("chargeNo")}</th>
              <th className="text-nowrap">{t("payer")}</th>
              <th className="text-nowrap">{t("receivedBy")}</th>
              <th className="text-nowrap">{t("amount")}</th>
              <th className="text-nowrap">{t("bankUsed")}</th>
              <th className="text-nowrap">{t("description")}</th>
              <th className="text-nowrap">
                {t("payment")} {t("status")}
              </th>
              <th className="text-nowrap">{t("date")}</th>
              <th className="text-nowrap">{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={10} />
            ) : charges.length > 0 ? (
              charges.map((item, i) => (
                <tr key={item.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>

                  <td className="text-nowrap">
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        background: "#f0f3f6",
                        padding: "2px 6px",
                        borderRadius: 4,
                      }}
                    >
                      {item.charge_number}
                    </span>
                  </td>

                  <td className="text-nowrap">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <strong>{item.payer?.name}</strong>
                      <small style={{ fontSize: 11, color: "#94a3b8" }}>
                        {item.payer?.email}
                      </small>
                      {item.payer?.phone && (
                        <small style={{ fontSize: 11, color: "#94a3b8" }}>
                          {item.payer.phone}
                        </small>
                      )}
                    </div>
                  </td>

                  <td className="text-nowrap">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <strong>{item.charged_by?.name || "—"}</strong>
                      <small style={{ fontSize: 11, color: "#94a3b8" }}>
                        {item.charged_by?.email}
                      </small>
                      {item.charged_by?.role && (
                        <small
                          style={{
                            fontSize: 10,
                            background: "#f0f3f6",
                            borderRadius: 4,
                            padding: "1px 5px",
                            width: "fit-content",
                            textTransform: "capitalize",
                          }}
                        >
                          {item.charged_by.role.replace("_", " ")}
                        </small>
                      )}
                    </div>
                  </td>

                  <td className="text-nowrap">
                    <strong>${(item.amount_cents / 100).toFixed(2)}</strong>
                    <small className="ms-1 text-muted">{item.currency}</small>
                  </td>

                  <td className="text-nowrap">{item.bank_name || "—"}</td>

                  <td
                    style={{
                      maxWidth: 200,
                      whiteSpace: "normal",
                      fontSize: 12,
                      color: "#475569",
                    }}
                  >
                    {item.description || "—"}
                  </td>

                  <td className="text-nowrap">
                    <span className={statusClass(item.payment_status)}>
                      {capitalizeFirstLetter(item.payment_status)}
                    </span>
                  </td>

                  <td className="text-nowrap">
                    {formatDateToLocal(item.created_at)}
                  </td>

                  <td className="text-nowrap">
                    {item.payment_status === "pending" &&
                    (item.charged_by?.id === user?.id ||
                      user?.role === "super_admin") ? (
                      <Button
                        className="rounded-circle"
                        onClick={() => setDeleteTargetId(item.id)}
                        title={t("delete")}
                      >
                        <RiDeleteBin6Fill />
                      </Button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="10">
                  {t("no")} {t("details")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pag?.pages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTargetId} centered>
        <Button className="closePopup" onClick={() => setDeleteTargetId(null)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-4 deleteHd">
            {`${t("are")} ${t("you")} ${t("sure")} ${t("want")} ${t("to")} ${t("delete")} ${t("this")} ${t("charge")}?`}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setDeleteTargetId(null)}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                t("btns.delete")
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReceivePaymentHistory;
